import Dialog from '../../1. Core/Controls/Dialog/Dialog';
import NumField from '../../1. Core/Controls/TextField/NumField';
import Select from '../../1. Core/Controls/Select/Select';
import {ServerAPI} from '../../1. Core/ServerAPI';
import DateInput from '../../1. Core/Controls/DateInput/DateInput';
import CheckBox from '../../1. Core/Controls/CheckBox/CheckBox';

let moment = require('moment');

const SPECIAL_FIGHTER_WEIGHT = '__special__';
const SPECIAL_BUNDLE_IAP = 'special_bundle';

export interface SpecialBundleRewards {
	coins: number;
	diamonds: number;
	boosterPack?: string;
	chestId?: string;
	avatarCategoryKey?: string;
	avatarItemLevel?: string;
	avatarCatalogId?: number;
	fighterCard?: string;
	fighterCards?: string[];
	fighterCardCatalogIds?: number[];
}

export interface SpecialBundle {
	id?: number;
	name_en: string;
	name_ru: string;
	name_es: string;
	name_pt: string;
	price_label?: string;
	iap_product_id?: string;
	display_order: number;
	active: boolean;
	starts_at?: string;
	ends_at?: string;
	rewards: SpecialBundleRewards;
}

interface FighterCardV2CatalogRow {
	catalogId: number;
	fullName: string;
	displayOrder: number;
	isChampion: boolean;
	weightClassKey: string;
	isSpecial?: boolean;
}

interface AvatarCatalogItem {
	id: number;
	level: string;
	categoryKey: string;
	nameRu: string;
	nameEn: string;
}

export default class EditSpecialBundleDialog extends Dialog {
	private _bundle: SpecialBundle;
	private _displayOrder: NumField;
	private _startsAt: DateInput;
	private _endsAt: DateInput;
	private _active: CheckBox;
	private _rewardCoins: NumField;
	private _rewardDiamonds: NumField;
	private _rewardCardPackageSelect: Select;
	private _rewardChestSelect: Select;
	private _rewardAvatarCategorySelect: Select;
	private _rewardAvatarLevelSelect: Select;
	private _rewardAvatarItemSelect: Select;
	private _fighterWeightSelect: Select;
	private _fighterCardSelect: Select;
	private _savedFighterCatalogId?: number;
	private _savedAvatarCatalogId?: number;

	constructor(props: {Bundle?: SpecialBundle; EvOkButtonClick?: () => void}) {
		super(props);
	}

	_initFields(settings: {Bundle?: SpecialBundle}) {
		this._bundle = settings.Bundle
			? JSON.parse(JSON.stringify(settings.Bundle))
			: {
				name_en: '',
				name_ru: '',
				name_es: '',
				name_pt: '',
				iap_product_id: SPECIAL_BUNDLE_IAP,
				display_order: 0,
				active: true,
				rewards: {coins: 0, diamonds: 0},
			};
		super._initFields(settings);
		this._Mode = 'FullHeight';
		this._Caption = this._bundle.id ? 'Edit special bundle' : 'New special bundle';
		this._CloseOnFrameMouseDown = true;
		this._ShowHeader = true;
		this._ShowCloseButton = true;
		this._OkRK = 'Save';
		this._AutoDestroy = true;
	}

	_render() {
		super._render();

		this._displayOrder = new NumField({ParentNode: this._contentNode, FloatingLabel: 'Display order'});
		this._startsAt = new DateInput({ParentNode: this._contentNode, FloatingLabel: 'Active from'});
		this._endsAt = new DateInput({ParentNode: this._contentNode, FloatingLabel: 'Active to'});
		this._active = new CheckBox({ParentNode: this._contentNode, Content: 'Active'});

		this._rewardCoins = new NumField({ParentNode: this._contentNode, FloatingLabel: 'Coins (required)'});
		this._rewardDiamonds = new NumField({ParentNode: this._contentNode, FloatingLabel: 'Diamonds (required)'});

		this._rewardCardPackageSelect = new Select({
			ParentNode: this._contentNode,
			FloatingLabel: 'Booster pack',
			Items: [
				{Content: '— None —', Value: null},
				{Content: 'Bronze', Value: 'bronze'},
				{Content: 'Silver', Value: 'silver'},
				{Content: 'Gold', Value: 'gold'},
			],
		});

		this._rewardChestSelect = new Select({
			ParentNode: this._contentNode,
			FloatingLabel: 'Collection chest',
			Items: [
				{Content: '— None —', Value: null},
				{Content: 'Small (2 cards)', Value: 'small'},
				{Content: 'Medium (4 cards)', Value: 'medium'},
				{Content: 'Large (6 cards)', Value: 'large'},
			],
		});

		this._rewardAvatarCategorySelect = new Select({
			ParentNode: this._contentNode,
			FloatingLabel: 'Avatar category',
			Items: [
				{Content: '— None —', Value: null},
				{Content: 'Boxers', Value: 'Boxers'},
				{Content: 'Ankle Support', Value: 'AnkleSupport'},
				{Content: 'Gloves', Value: 'Gloves'},
				{Content: 'Training', Value: 'Training'},
				{Content: 'Equipment', Value: 'Equipment'},
				{Content: 'Sports Nutrition', Value: 'SportsNutrition'},
				{Content: 'Shorts', Value: 'Shorts'},
			],
			EvItemSelected: () => this.loadAvatarItems(),
		});

		this._rewardAvatarLevelSelect = new Select({
			ParentNode: this._contentNode,
			FloatingLabel: 'Avatar level',
			Items: [
				{Content: '— None —', Value: null},
				{Content: 'Basic', Value: 'basic'},
				{Content: 'Pro', Value: 'pro'},
				{Content: 'Epic', Value: 'epic'},
			],
			EvItemSelected: () => this.loadAvatarItems(),
		});

		this._rewardAvatarItemSelect = new Select({
			ParentNode: this._contentNode,
			FloatingLabel: 'Avatar item',
		});

		this._fighterWeightSelect = new Select({
			ParentNode: this._contentNode,
			FloatingLabel: 'Fighter card v2 weight',
			EvItemSelected: () => this.loadFighterCards(),
		});

		this._fighterCardSelect = new Select({
			ParentNode: this._contentNode,
			FloatingLabel: 'Fighter card v2',
		});

		this.refresh();
	}

	async reloadFighterWeights() {
		const weights = await ServerAPI.get<string[]>('/fighter-cards/v2/weight-classes');
		const items = [
			{Content: '— None —', Value: null},
			{Content: 'Special', Value: SPECIAL_FIGHTER_WEIGHT},
			...(weights || []).map(w => ({Content: w, Value: w})),
		];
		this._fighterWeightSelect.setItems(items);
	}

	async loadFighterCards() {
		const weight = this._fighterWeightSelect.getValue();
		if (!weight) {
			this._fighterCardSelect.setItems([{Content: '— None —', Value: null}]);
			this._fighterCardSelect.setValue(null, false);
			return;
		}

		let rows: FighterCardV2CatalogRow[];
		if (weight === SPECIAL_FIGHTER_WEIGHT) {
			rows = await ServerAPI.get<FighterCardV2CatalogRow[]>('/fighter-cards/v2/catalog/special');
		} else {
			rows = await ServerAPI.get<FighterCardV2CatalogRow[]>(
				`/fighter-cards/v2/catalog?weightClassKey=${encodeURIComponent(weight)}`,
			);
		}

		const items = [
			{Content: '— None —', Value: null},
			...rows.map(r => ({
				Content: `${r.displayOrder}. ${r.fullName}${r.isSpecial ? ' (special)' : ''}${r.isChampion ? ' (champion)' : ''}`,
				Value: r.catalogId,
				Data: r,
			})),
		];
		this._fighterCardSelect.setItems(items);

		if (this._savedFighterCatalogId && items.some(i => i.Value === this._savedFighterCatalogId)) {
			this._fighterCardSelect.setValue(this._savedFighterCatalogId, false);
			this._savedFighterCatalogId = undefined;
		} else {
			this._fighterCardSelect.setValue(null, false);
		}
	}

	async restoreFighterSelection(catalogId: number) {
		this._savedFighterCatalogId = catalogId;

		const special = await ServerAPI.get<FighterCardV2CatalogRow[]>('/fighter-cards/v2/catalog/special');
		if (special.some(r => r.catalogId === catalogId)) {
			this._fighterWeightSelect.setValue(SPECIAL_FIGHTER_WEIGHT, false);
			await this.loadFighterCards();
			return;
		}

		const weights = await ServerAPI.get<string[]>('/fighter-cards/v2/weight-classes');
		for (const weight of weights || []) {
			const rows = await ServerAPI.get<FighterCardV2CatalogRow[]>(
				`/fighter-cards/v2/catalog?weightClassKey=${encodeURIComponent(weight)}`,
			);
			if (rows.some(r => r.catalogId === catalogId)) {
				this._fighterWeightSelect.setValue(weight, false);
				await this.loadFighterCards();
				return;
			}
		}

		this._fighterWeightSelect.setValue(null, false);
		await this.loadFighterCards();
	}

	async loadAvatarItems() {
		const category = this._rewardAvatarCategorySelect.getValue();
		const level = this._rewardAvatarLevelSelect.getValue();
		if (!category || !level) {
			this._rewardAvatarItemSelect.setItems([{Content: '— None —', Value: null}]);
			this._rewardAvatarItemSelect.setValue(null, false);
			return;
		}

		const rows = await ServerAPI.get<AvatarCatalogItem[]>(
			`/avatar/catalog-items?categoryKey=${encodeURIComponent(category)}&level=${encodeURIComponent(level)}`,
		);
		const items = [
			{Content: '— None —', Value: null},
			...rows.map(r => ({
				Content: `${r.nameRu || r.nameEn} (#${r.id})`,
				Value: r.id,
				Data: r,
			})),
		];
		this._rewardAvatarItemSelect.setItems(items);

		if (this._savedAvatarCatalogId && items.some(i => i.Value === this._savedAvatarCatalogId)) {
			this._rewardAvatarItemSelect.setValue(this._savedAvatarCatalogId, false);
			this._savedAvatarCatalogId = undefined;
		} else {
			this._rewardAvatarItemSelect.setValue(null, false);
		}
	}

	async refresh() {
		const b = this._bundle;
		const r = b.rewards || {coins: 0, diamonds: 0};

		this._displayOrder.setValue(b.display_order ?? 0);
		this._startsAt.setValue(b.starts_at ? moment(b.starts_at).toDate() : null);
		this._endsAt.setValue(b.ends_at ? moment(b.ends_at).toDate() : null);
		this._active.setValue(b.active !== false);
		this._rewardCoins.setValue(r.coins || '');
		this._rewardDiamonds.setValue(r.diamonds || '');
		this._rewardCardPackageSelect.setValue(r.boosterPack || null);
		this._rewardChestSelect.setValue(r.chestId || null);
		this._rewardAvatarCategorySelect.setValue(r.avatarCategoryKey || null);
		this._rewardAvatarLevelSelect.setValue(r.avatarItemLevel || null);
		this._savedAvatarCatalogId = r.avatarCatalogId || undefined;

		await this.reloadFighterWeights();
		const fighterCatalogId = r.fighterCardCatalogIds?.[0];
		if (fighterCatalogId) {
			await this.restoreFighterSelection(fighterCatalogId);
		} else {
			this._fighterWeightSelect.setValue(null, false);
			await this.loadFighterCards();
		}

		await this.loadAvatarItems();
	}

	formatDateTime(value: Date | null): string | null {
		return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : null;
	}

	async _onOkButtonClick() {
		const coins = Number(this._rewardCoins.getValue()) || 0;
		const diamonds = Number(this._rewardDiamonds.getValue()) || 0;
		if (coins <= 0 || diamonds <= 0) {
			alert('Coins and diamonds are required and must be > 0');
			return;
		}

		const avatarItem = this._rewardAvatarItemSelect.getSelectedItem()?.Data as AvatarCatalogItem | undefined;
		const avatarCatalogId = this._rewardAvatarItemSelect.getValue();
		const fighterCatalogId = this._fighterCardSelect.getValue();

		this._bundle.iap_product_id = SPECIAL_BUNDLE_IAP;
		this._bundle.price_label = null;
		this._bundle.display_order = Number(this._displayOrder.getValue()) || 0;
		this._bundle.active = this._active.getValue();
		this._bundle.starts_at = this.formatDateTime(this._startsAt.getValue());
		this._bundle.ends_at = this.formatDateTime(this._endsAt.getValue());
		this._bundle.rewards = {
			coins,
			diamonds,
			boosterPack: this._rewardCardPackageSelect.getValue() || undefined,
			chestId: this._rewardChestSelect.getValue() || undefined,
			avatarCategoryKey: avatarItem?.categoryKey || this._rewardAvatarCategorySelect.getValue() || undefined,
			avatarItemLevel: avatarItem?.level || this._rewardAvatarLevelSelect.getValue() || undefined,
			avatarCatalogId: avatarCatalogId || undefined,
			fighterCardCatalogIds: fighterCatalogId ? [Number(fighterCatalogId)] : [],
		};

		if (this._bundle.id) {
			await ServerAPI.put(`/shop/special-bundles/${this._bundle.id}`, this._bundle);
		} else {
			const id = await ServerAPI.post<number>('/shop/special-bundles', this._bundle);
			this._bundle.id = id;
		}

		this.EvOkButtonClick.fire(this);
		this.hide();
	}
}
